import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useWindowSize } from 'common/hook/useWindowSize';
import { IAnyType } from 'common/types/common';
import { DrawSVGPlugin } from 'gsap/dist/DrawSVGPlugin';
import gsap from 'gsap/dist/gsap';
import { MorphSVGPlugin } from 'gsap/dist/MorphSVGPlugin';
import ScrollToPlugin from 'gsap/dist/ScrollToPlugin';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import { TooltipContext } from 'layout';
import { useTranslation } from 'next-i18next';

gsap.registerPlugin(DrawSVGPlugin, MorphSVGPlugin, ScrollToPlugin, ScrollTrigger);

const Item = ({ short, value }: IAnyType) => {
  const { handleSetTooltip, tooltip } = useContext(TooltipContext);

  const onMouseEnter = () => {
    if (tooltip !== value) {
      handleSetTooltip(value);
    }
  };

  const onMouseLeave = () => {
    handleSetTooltip('');
  };

  return (
    <div
      className="cursor-pointer bg-white/10 p-3 py-2 text-[16px] hover:border-primary hover:text-primary"
      onMouseEnter={onMouseEnter}
      onMouseOut={onMouseLeave}>
      {short}
    </div>
  );
};

export const StrategyItems: React.FC = () => {
  const { t } = useTranslation('pageExpertise');
  const containerRef = useRef(null);
  const { dimensions } = useWindowSize();

  const [elementWidth, setElementWidth] = useState(0);

  useEffect(() => {
    const dataOne = {
      opacity: 0,
      duration: 2,
      scrollTrigger: {
        trigger: '#strategy-item-panel-2',
        start: 'top 50%',
        end: 'top 45%',
        scrub: true,
      },
    };
    gsap.to('#inner-img-6', {
      duration: 1,
      opacity: 0,
      scrollTrigger: { ...dataOne.scrollTrigger, trigger: '#strategy-item-panel-2' },
    });
    gsap.to('#inner-img-5', {
      duration: 1,
      opacity: 0,
      scrollTrigger: { ...dataOne.scrollTrigger, trigger: '#strategy-item-panel-3' },
    });
    gsap.to('#inner-img-4', {
      duration: 1,
      opacity: 0,
      scrollTrigger: { ...dataOne.scrollTrigger, trigger: '#strategy-item-panel-4' },
    });
    gsap.to('#inner-img-3', {
      duration: 1,
      opacity: 0,
      scrollTrigger: { ...dataOne.scrollTrigger, trigger: '#strategy-item-panel-5' },
    });
    gsap.to('#inner-img-2', {
      duration: 1,
      opacity: 0,
      scrollTrigger: { ...dataOne.scrollTrigger, trigger: '#strategy-item-panel-6' },
    });
  }, []);

  useEffect(() => {
    const initial = document.getElementById('inner-img-1');
    const data = { duration: 1, immediateRender: false };
    const scrollData = { scrub: true, start: 'top 90%', end: 'top 40%' };
    gsap.fromTo(
      initial,
      { morphSVG: '#inner-img-1' },
      {
        ...data,
        morphSVG: '#inner-img-2',
        scrollTrigger: { ...scrollData, trigger: '#strategy-item-panel-2' },
      },
    );
    gsap.fromTo(
      initial,
      { morphSVG: '#inner-img-2' },
      {
        ...data,
        morphSVG: '#inner-img-3',
        scrollTrigger: { ...scrollData, trigger: '#strategy-item-panel-3' },
      },
    );
    gsap.fromTo(
      initial,
      { morphSVG: '#inner-img-3' },
      {
        ...data,
        morphSVG: '#inner-img-4',
        scrollTrigger: { ...scrollData, trigger: '#strategy-item-panel-4' },
      },
    );
    gsap.fromTo(
      initial,
      { morphSVG: '#inner-img-4' },
      {
        ...data,
        morphSVG: '#inner-img-5',
        scrollTrigger: { ...scrollData, trigger: '#strategy-item-panel-5' },
      },
    );
    gsap.fromTo(
      initial,
      { morphSVG: '#inner-img-5' },
      {
        ...data,
        morphSVG: '#inner-img-6',
        scrollTrigger: { ...scrollData, trigger: '#strategy-item-panel-6' },
      },
    );
  }, []);

  useEffect(() => {
    const data = {
      drawSVG: '100%',
      ease: 'none',
      scrollTrigger: { scrub: true, start: 'top 0', end: () => `${window.innerHeight - 100}px` },
    };
    gsap.fromTo(
      '#line1',
      { drawSVG: 0 },
      {
        drawSVG: '100%',
        ease: 'none',
        scrollTrigger: { trigger: '#strategy', scrub: true, start: 'top 1000px', end: 'top 0' },
      },
    );
    gsap.fromTo(
      '#line2',
      { drawSVG: 0 },
      { ...data, scrollTrigger: { ...data.scrollTrigger, trigger: '#strategy-item-panel-1' } },
    );
    gsap.fromTo(
      '#line3',
      { drawSVG: 0 },
      { ...data, scrollTrigger: { ...data.scrollTrigger, trigger: '#strategy-item-panel-2' } },
    );
    gsap.fromTo(
      '#line4',
      { drawSVG: 0 },
      { ...data, scrollTrigger: { ...data.scrollTrigger, trigger: '#strategy-item-panel-3' } },
    );
    gsap.fromTo(
      '#line5',
      { drawSVG: 0 },
      { ...data, scrollTrigger: { ...data.scrollTrigger, trigger: '#strategy-item-panel-4' } },
    );
    gsap.fromTo(
      '#line6',
      { drawSVG: 0 },
      { ...data, scrollTrigger: { ...data.scrollTrigger, trigger: '#strategy-item-panel-5' } },
    );
  }, []);

  useLayoutEffect(() => {
    if (containerRef.current) {
      const containerWidth = (containerRef.current as IAnyType).offsetWidth;
      const sectionWidth = dimensions.window.width;
      setElementWidth((containerWidth / 5) * 2 + (sectionWidth - containerWidth) / 2);
    }
  }, [dimensions.window.width]);

  const renderHexagonLines = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="540"
      height="375"
      viewBox="0 0 4849 4187"
      fill="none">
      <line
        id="line1"
        x1="1204.88"
        y1="76.0001"
        x2="3650.88"
        y2="71.9886"
        stroke="#F2C301"
        strokeWidth="142"
      />
      <line
        id="line2"
        x1="3589.7"
        y1="38.8785"
        x2="4786.7"
        y2="2141.88"
        stroke="#F2C301"
        strokeWidth="142"
      />
      <line
        id="line3"
        x2="3578.62"
        y2="4147.48"
        x1="4783.61"
        y1="2075.31"
        stroke="#F2C301"
        strokeWidth="142"
      />
      <line
        id="line4"
        x2="1199.88"
        y2="4115.01"
        x1="3639.88"
        y1="4111.01"
        stroke="#F2C301"
        strokeWidth="142"
      />
      <line
        id="line5"
        x2="62.7048"
        y2="2044.88"
        x1="1259.7"
        y1="4147.88"
        stroke="#F2C301"
        strokeWidth="142"
      />
      <line
        id="line6"
        x1="62.2324"
        y1="2113.18"
        x2="1267.22"
        y2="41.0102"
        stroke="#F2C301"
        strokeWidth="142"
      />
    </svg>
  );

  const renderHexagon = () => (
    <div className="relative h-[620vh] w-full">
      <div id="panel-img" className="sticky top-0 flex items-center justify-center">
        <div className="relative h-[100vh] w-full">
          <div className="absolute bottom-0 left-0 right-0 top-0 flex items-center justify-end">
            {renderHexagonLines()}
          </div>
          <div className="absolute bottom-0 left-0 right-0 top-0 flex items-center justify-end">
            <div className="w-[320px]">{renderIcon()}</div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderIcon = () => (
    <div className="strategy-panel-inner-img">
      <svg
        stroke="white"
        fill="white"
        strokeWidth="0"
        viewBox="0 0 16 16"
        color="white"
        height="100"
        width="100"
        xmlns="http://www.w3.org/2000/svg">
        <path
          id="inner-img-1"
          d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"
        />

        <path
          id="inner-img-2"
          className="hidden"
          d="M5.854 4.854a.5.5 0 1 0-.708-.708l-3.5 3.5a.5.5 0 0 0 0 .708l3.5 3.5a.5.5 0 0 0 .708-.708L2.707 8l3.147-3.146zm4.292 0a.5.5 0 0 1 .708-.708l3.5 3.5a.5.5 0 0 1 0 .708l-3.5 3.5a.5.5 0 0 1-.708-.708L13.293 8l-3.147-3.146z"
        />

        <path
          id="inner-img-3"
          className="hidden"
          d="M4.708 5.578L2.061 8.224l2.647 2.646-.708.708-3-3V7.87l3-3 .708.708zm7-.708L11 5.578l2.647 2.646L11 10.87l.708.708 3-3V7.87l-3-3zM4.908 13l.894.448 5-10L9.908 3l-5 10z"
        />

        <path
          id="inner-img-4"
          className="hidden"
          d="M8 8c.828 0 1.5-.895 1.5-2S8.828 4 8 4s-1.5.895-1.5 2S7.172 8 8 8Z M11.953 8.81c-.195-3.388-.968-5.507-1.777-6.819C9.707 1.233 9.23.751 8.857.454a3.495 3.495 0 0 0-.463-.315A2.19 2.19 0 0 0 8.25.064.546.546 0 0 0 8 0a.549.549 0 0 0-.266.073 2.312 2.312 0 0 0-.142.08 3.67 3.67 0 0 0-.459.33c-.37.308-.844.803-1.31 1.57-.805 1.322-1.577 3.433-1.774 6.756l-1.497 1.826-.004.005A2.5 2.5 0 0 0 2 12.202V15.5a.5.5 0 0 0 .9.3l1.125-1.5c.166-.222.42-.4.752-.57.214-.108.414-.192.625-.281l.198-.084c.7.428 1.55.635 2.4.635.85 0 1.7-.207 2.4-.635.067.03.132.056.196.083.213.09.413.174.627.282.332.17.586.348.752.57l1.125 1.5a.5.5 0 0 0 .9-.3v-3.298a2.5 2.5 0 0 0-.548-1.562l-1.499-1.83ZM12 10.445v.055c0 .866-.284 1.585-.75 2.14.146.064.292.13.425.199.39.197.8.46 1.1.86L13 14v-1.798a1.5 1.5 0 0 0-.327-.935L12 10.445ZM4.75 12.64C4.284 12.085 4 11.366 4 10.5v-.054l-.673.82a1.5 1.5 0 0 0-.327.936V14l.225-.3c.3-.4.71-.664 1.1-.861.133-.068.279-.135.425-.199ZM8.009 1.073c.063.04.14.094.226.163.284.226.683.621 1.09 1.28C10.137 3.836 11 6.237 11 10.5c0 .858-.374 1.48-.943 1.893C9.517 12.786 8.781 13 8 13c-.781 0-1.517-.214-2.057-.607C5.373 11.979 5 11.358 5 10.5c0-4.182.86-6.586 1.677-7.928.409-.67.81-1.082 1.096-1.32.09-.076.17-.135.236-.18Z M9.479 14.361c-.48.093-.98.139-1.479.139-.5 0-.999-.046-1.479-.139L7.6 15.8a.5.5 0 0 0 .8 0l1.079-1.439Z"
        />

        <path
          id="inner-img-5"
          className="hidden"
          fillRule="evenodd"
          d="M.11 3.187A.5.5 0 0 1 .5 3h13a.5.5 0 0 1 .488.608l-.22.991a3.001 3.001 0 0 1-1.3 5.854l-.132.59A2.5 2.5 0 0 1 9.896 13H4.104a2.5 2.5 0 0 1-2.44-1.958L.012 3.608a.5.5 0 0 1 .098-.42Zm12.574 6.288a2 2 0 0 0 .866-3.899l-.866 3.9ZM1.124 4l1.516 6.825A1.5 1.5 0 0 0 4.104 12h5.792a1.5 1.5 0 0 0 1.464-1.175L12.877 4H1.123Z"
        />
        <path
          id="inner-img-6"
          className="hidden"
          d="M11 5.466V4H5a4 4 0 0 0-3.584 5.777.5.5 0 1 1-.896.446A5 5 0 0 1 5 3h6V1.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384l-2.36 1.966a.25.25 0 0 1-.41-.192Zm3.81.086a.5.5 0 0 1 .67.225A5 5 0 0 1 11 13H5v1.466a.25.25 0 0 1-.41.192l-2.36-1.966a.25.25 0 0 1 0-.384l2.36-1.966a.25.25 0 0 1 .41.192V12h6a4 4 0 0 0 3.585-5.777.5.5 0 0 1 .225-.67Z"
        />
      </svg>
    </div>
  );

  const renderSections = () => {
    const strategyItems: IAnyType = t('strategyItems', { returnObjects: true });
    return strategyItems?.map((item: IAnyType, index: number) => (
      <div
        id={`strategy-item-panel-${index + 1}`}
        key={item.title}
        className={clsx(
          'bg-gradient lg:sticky lg:top-0',
          { 'pt-20': index === 0 },
          { 'pt-10': index !== 0 },
        )}>
        <div className="container mx-auto" ref={containerRef}>
          <div className="grid flex-col justify-center lg:sticky lg:top-0 lg:min-h-[100vh] lg:grid-cols-5">
            <div className="col-span-2" />
            <article className="col-span-3 flex flex-col justify-center px-5 pb-20">
              <div className="mb-8 flex flex-col">
                {/* <h3 className="mb-1 mr-2 border-b-px border-b-primary pb-1 font-black w-10">
                  0{index + 1}
                </h3> */}
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="h-px w-10 bg-primary" />
                    <h3 className="font-semibold uppercase text-primary">{item.title}</h3>
                  </div>
                </div>
              </div>
              {!!item.items?.length && (
                <div className="mb-8 flex flex-wrap gap-1.5 lg:max-w-[700px] xl:max-w-[800px]">
                  {item.items.map((innerItem: IAnyType) => (
                    <Item key={innerItem.key} short={innerItem.key} value={innerItem.value} />
                  ))}
                </div>
              )}
              {!!item.paragraphOne && (
                <div
                  className="mb-8 text-white lg:max-w-[800px] xl:max-w-[1000px]"
                  dangerouslySetInnerHTML={{ __html: item.paragraphOne }}
                />
              )}

              {!!item.paragraphTwo && (
                <div
                  className="mb-8 text-white lg:max-w-[800px] 2xl:max-w-[1000px]"
                  dangerouslySetInnerHTML={{ __html: item.paragraphTwo }}
                />
              )}
            </article>
          </div>
        </div>
      </div>
    ));
  };

  return (
    <section id="strategy-panel" className="bg-gradient relative">
      <div className="relative">{renderSections()}</div>

      <div className="z-[100 absolute left-0 top-0 hidden lg:block" style={{ width: elementWidth }}>
        <div className="w-full lg:h-[612vh]">
          <div className="relative">{renderHexagon()}</div>
        </div>
      </div>
    </section>
  );
};

export default StrategyItems;
